/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_OPTIONAL_FIELDS_ADD_LOCATION
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Added userCity, userState, and userCountry to USER_OPTIONAL_FIELDS list for normalization.
 * 
 * Related Screens / Modules:
 * - client/src/utils/formHelpers.ts
 * - client/src/components/setup/user-profile/hooks/useUserForm.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FORM_HELPERS_UTILITY
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Created reusable form helper utilities to prevent optional field deletion bugs.
 * Ensures empty strings are sent for optional/deletable fields.
 *
 * Related Screens / Modules:
 * - All form components
 */

/**
 * Normalizes optional field values for API submission
 * Ensures empty/null/undefined values are sent as empty strings
 * 
 * @param value - The field value to normalize
 * @returns Empty string for null/undefined, trimmed string otherwise
 */
export const normalizeOptionalField = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  return String(value);
};

/**
 * Normalizes all optional fields in form data
 * 
 * @param formData - The form data object
 * @param optionalFields - Array of field names to normalize
 * @returns New object with normalized optional fields
 */
export const normalizeOptionalFields = <T extends Record<string, any>>(
  formData: T,
  optionalFields: readonly (keyof T)[]
): T => {
  const normalized = { ...formData };
  
  optionalFields.forEach(field => {
    if (normalized[field] !== undefined) {
      normalized[field] = normalizeOptionalField(normalized[field]) as T[keyof T];
    }
  });
  
  return normalized;
};

/**
 * List of common optional fields for user profiles
 */
export const USER_OPTIONAL_FIELDS = [
  "jobTitle",
  "tel",
  "bio",
  "linkedIn",
  "x",
  "instagram",
  "facebook",
  "userCity",
  "userState",
  "userCountry",
  "broadcastSignatureHtml"
] as const;

/**
 * List of common optional fields for company profiles
 */
export const COMPANY_OPTIONAL_FIELDS = [
  "companyLinkedIn",
  "companyFacebook",
  "companyX",
  "companyInstagram",
  "website",
  "aboutCompany",
  "telCompany",
  "companyEmail",
  "address1",
  "address2",
  "city",
  "state",
  "zip",
  "country",
  "services"
] as const;
